import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon } from "../icons";
import { barColors } from "../theme";
import { T } from "../routes/Translate";

// Umbral en px que hay que arrastrar hacia abajo para disparar el refresh,
// y tope visual para que el bloque no se estire de más.
const PULL_THRESHOLD = 70;
const MAX_PULL = 100;
const RESISTANCE = 2.2;

const getWindowScrollTop = () =>
  window.scrollY || document.documentElement.scrollTop || 0;

// Un elemento cuenta como "el que scrollea" si tiene overflow vertical
// habilitado y contenido más alto que su propio alto visible.
const isScrollableNode = (node) => {
  const style = window.getComputedStyle(node);
  const overflowY = style.overflowY;
  return (
    (overflowY === "auto" || overflowY === "scroll") &&
    node.scrollHeight > node.clientHeight + 1
  );
};

// Sube desde el punto donde arrancó el toque buscando el contenedor con
// scroll propio de esa pantalla (chats, listados con su propio overflow,
// etc.). Si ninguno de sus ancestros scrollea, la pantalla usa el scroll
// de la ventana.
const findScrollableAncestor = (target) => {
  let node = target instanceof Element ? target : target?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    if (isScrollableNode(node)) return node;
    node = node.parentElement;
  }
  return null;
};

// El wrapper que envuelve <Router> en App.js. Un transform en un ancestro
// pasa a ser el "containing block" de sus descendientes position:fixed, así
// que empujarlo alcanza para desplazar de una sola vez el AppBar y el
// BottomToolbar de cualquier pantalla (cada una arma los suyos), sin tener
// que tocarlos uno por uno.
const getAppShell = () => document.getElementById("app-shell");

/**
 * Pull-to-refresh global: se monta una sola vez (App.js) y escucha touch
 * sobre todo el documento; el gesto solo arranca cuando la ventana está
 * scrolleada hasta arriba del todo, para no pelear con el scroll normal de
 * cada pantalla.
 *
 * A diferencia de la versión anterior (una insignia flotando por encima de
 * todo), este bloque empuja #app-shell hacia abajo con el mismo alto que va
 * tomando: el bloque aparece en el hueco real que se abre entre la status
 * bar nativa y el AppBar de la pantalla, en vez de taparlo.
 */
const PullToRefresh = ({ onRefresh, disabled = false }) => {
  const touchStartY = useRef(0);
  const pulling = useRef(false);
  // Contenedor con scroll propio detectado al arrancar el gesto (o null si
  // la pantalla usa el scroll de la ventana). Se resuelve una sola vez por
  // gesto para no recorrer el DOM en cada touchmove.
  const activeScrollNode = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullDistanceRef = useRef(0);
  const refreshTriggered = useRef(false);
  pullDistanceRef.current = pullDistance;

  const getActiveScrollTop = useCallback(() => {
    const node = activeScrollNode.current;
    return node ? node.scrollTop : getWindowScrollTop();
  }, []);

  const reset = useCallback(() => {
    pulling.current = false;
    activeScrollNode.current = null;
    setPullDistance(0);
  }, []);

  // Si se desactiva a mitad de gesto (p.ej. se navegó a una ruta excluida),
  // cancelamos cualquier arrastre en curso.
  useEffect(() => {
    if (disabled) reset();
  }, [disabled, reset]);

  // Única fuente de verdad para empujar #app-shell: cualquier cambio de
  // pullDistance (arrastre en vivo o snap de vuelta a 0) se refleja acá.
  useEffect(() => {
    const shell = getAppShell();
    if (!shell) return;
    shell.style.transition = pulling.current ? "none" : "transform 0.25s ease";
    shell.style.transform = pullDistance ? `translateY(${pullDistance}px)` : "";
  }, [pullDistance]);

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (disabled || refreshing) return;
      // Con un panel, diálogo o menú abierto (todos montan un MuiModal-root;
      // los cerrados que quedan montados llevan MuiModal-hidden) no se
      // refresca: arrastrar hacia abajo es para cerrarlo o scrollearlo.
      if (document.querySelector(".MuiModal-root:not(.MuiModal-hidden)")) return;
      activeScrollNode.current = findScrollableAncestor(e.target);
      if (getActiveScrollTop() > 0) {
        // La sección en la que se tocó todavía tiene scroll hacia arriba
        // pendiente: no es momento de refrescar.
        activeScrollNode.current = null;
        return;
      }
      pulling.current = true;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (!pulling.current) return;
      if (getActiveScrollTop() > 0) {
        reset();
        return;
      }
      const currentY = e.touches[0].clientY;
      const delta = currentY - touchStartY.current;
      if (delta <= 0) {
        // El usuario volvió hacia arriba: recalcular el ancla desde acá.
        touchStartY.current = currentY;
        setPullDistance(0);
        return;
      }
      const next = Math.min(delta / RESISTANCE, MAX_PULL);
      pullDistanceRef.current = next;
      setPullDistance(next);
      // Evita el rebote nativo del navegador mientras se arrastra.
      if (e.cancelable) e.preventDefault();
    };

    const handleTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      activeScrollNode.current = null;
      // Se decide con el ref y no dentro de un updater de setState: React puede
      // ejecutar los updaters dos veces y eso disparaba onRefresh (reload) doble.
      if (pullDistanceRef.current >= PULL_THRESHOLD && !refreshTriggered.current) {
        refreshTriggered.current = true;
        setRefreshing(true);
        setPullDistance(PULL_THRESHOLD);
        Promise.resolve()
          .then(() => onRefresh?.())
          .catch(() => {})
          .finally(() => {
            refreshTriggered.current = false;
            setRefreshing(false);
            setPullDistance(0);
          });
      } else {
        setPullDistance(0);
      }
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [disabled, refreshing, onRefresh, reset, getActiveScrollTop]);

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const visible = pullDistance > 0 || refreshing;
  const label = refreshing ? (
    <T>Actualizando…</T>
  ) : progress >= 1 ? (
    <T>Soltá para actualizar</T>
  ) : (
    <T>Deslizá para actualizar</T>
  );

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: pullDistance,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 20000,
        background: barColors.background,
        boxShadow: visible ? `0 1px 0 ${barColors.border}` : "none",
        opacity: visible ? 1 : 0,
        transition: pulling.current
          ? "none"
          : "height 0.25s ease, opacity 0.25s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: barColors.text,
          opacity: Math.max(progress, refreshing ? 1 : 0.2),
          transition: pulling.current ? "none" : "opacity 0.25s ease",
          whiteSpace: "nowrap",
        }}
      >
        {refreshing ? (
          <CircularProgress size={18} thickness={5} sx={{ color: barColors.icon }} />
        ) : (
          <KeyboardArrowDownRoundedIcon
            sx={{
              color: barColors.icon,
              transform: `rotate(${progress >= 1 ? 180 : 0}deg)`,
              transition: pulling.current ? "none" : "transform 0.2s ease",
            }}
          />
        )}
        <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
          {label}
        </Box>
      </Box>
    </Box>
  );
};

export default PullToRefresh;
